/**
 * Drawing activity for the waiting screen (Round 2 §1.1). The drawing screen notes
 * every drawing action and every bit of pointer input; the state poll carries a
 * summary to the server. Counts and durations only — never what was drawn.
 *
 * The count is monotonic: undoing ten times while fixing a face reads as busy,
 * not as losing progress. It survives a refresh, so it never goes backwards.
 */

export interface ActivityReport {
  panelId: string;
  actions: number;
  /** Milliseconds since the last drawing input. A duration, so clock skew can't matter. */
  idleMs: number;
}

interface PanelActivity {
  actions: number;
  /** Epoch ms of the last pointer input on the canvas; 0 before any. */
  lastInputAt: number;
}

const PREFIX = 'panels:activity:';
/** Mid-stroke input is frequent; it only needs saving often enough to survive a refresh. */
const INPUT_SAVE_INTERVAL_MS = 5000;

const cache = new Map<string, PanelActivity>();
let lastSaved = 0;
let current: string | null = null;

function read(panelId: string): PanelActivity {
  let entry = cache.get(panelId);
  if (entry) return entry;
  entry = { actions: 0, lastInputAt: 0 };
  try {
    const raw = localStorage.getItem(PREFIX + panelId);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PanelActivity>;
      entry = { actions: Number(parsed.actions) || 0, lastInputAt: Number(parsed.lastInputAt) || 0 };
    }
  } catch {
    /* a fresh count is fine */
  }
  cache.set(panelId, entry);
  return entry;
}

function save(panelId: string, entry: PanelActivity): void {
  lastSaved = Date.now();
  try {
    localStorage.setItem(PREFIX + panelId, JSON.stringify(entry));
  } catch {
    /* activity is a nicety; never let it get in the way of drawing */
  }
}

/** The panel on screen right now — the one the poll reports on. */
export function trackPanel(panelId: string | null): void {
  current = panelId;
}

/** A drawing action: a stroke, undo, redo, straighten or erase. */
export function noteAction(panelId: string): void {
  const entry = read(panelId);
  entry.actions += 1;
  entry.lastInputAt = Date.now();
  save(panelId, entry);
}

/** Any pointer input on the canvas, including mid-stroke movement. */
export function noteInput(panelId: string): void {
  const entry = read(panelId);
  entry.lastInputAt = Date.now();
  if (Date.now() - lastSaved > INPUT_SAVE_INTERVAL_MS) save(panelId, entry);
}

/**
 * What the next poll should say. Nothing until the first input: before that the
 * server measures from when the assignment began.
 */
export function activityReport(): ActivityReport | null {
  if (!current) return null;
  const entry = read(current);
  if (entry.lastInputAt === 0) return null;
  return { panelId: current, actions: entry.actions, idleMs: Math.max(0, Date.now() - entry.lastInputAt) };
}

/** The panel is sent; its activity is no longer anyone's business. */
export function clearActivity(panelId: string): void {
  cache.delete(panelId);
  if (current === panelId) current = null;
  try {
    localStorage.removeItem(PREFIX + panelId);
  } catch {
    /* ignore */
  }
}
